import React from "react";

// Small classnames helper
export function cx(...xs: Array<string | false | null | undefined>) {
  return xs.filter(Boolean).join(" ");
}


/* Form fields (consistent across modals) */
export const field = {
  label: "mb-1 block text-sm text-[var(--sc-muted)]",
  input:
    "w-full rounded-lg border border-[var(--sc-border)] bg-[var(--sc-surface)] p-2.5 text-sm text-[var(--sc-text)] " +
    "placeholder:text-[var(--sc-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--sc-amber)] focus:border-[var(--sc-amber)]",
  textarea:
    "w-full rounded-lg border border-[var(--sc-border)] bg-[var(--sc-surface)] p-2.5 text-sm text-[var(--sc-text)] whitespace-pre-wrap " +
    "placeholder:text-[var(--sc-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--sc-amber)] focus:border-[var(--sc-amber)]",
  select:
    "w-full rounded-lg border border-[var(--sc-border)] bg-[var(--sc-surface)] p-2.5 text-sm text-[var(--sc-text)] " +
    "focus:outline-none focus:ring-2 focus:ring-[var(--sc-amber)] focus:border-[var(--sc-amber)]",
  checkbox:
    "h-4 w-4 rounded border-[var(--sc-border)] text-[var(--sc-amber)] focus:ring-[var(--sc-amber)]",
};

/* Modal theme tokens */
export const modalTheme = {
  overlay: "fixed inset-0 bg-black/25",
  container: "fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6",
  panel:
    "relative w-full rounded-[14px] bg-[var(--sc-surface)] shadow-[0_20px_60px_rgba(0,0,0,0.28)] " +
    "transition-all",
  header: "flex items-center justify-between gap-2 px-5 pt-5 pb-4 border-b border-[var(--sc-border)]",
  title: "text-[15px] font-semibold text-[var(--sc-text)]",
  body: "px-5 py-4 space-y-4 overflow-y-auto max-h-[calc(100vh-14rem)]",
  footer: "px-5 pb-5 flex items-center justify-between gap-2",
  sizes: {
    sm: "max-w-md",
    md: "max-w-lg",
    lg: "max-w-2xl",
    xl: "max-w-4xl",
  } as const,
};


/* =========================
   Minimal, professional buttons
   - Neutral-only palette
   - Primary: dark filled
   - Danger: red text, no outline
========================= */
type ButtonVariant = "solid" | "outline" | "ghost" | "link";
type ButtonTone = "neutral" | "primary" | "danger"; // 'primary' aliases to neutral
type ButtonSize = "xs" | "sm" | "md" | "lg";

const baseBtn =
  "inline-flex items-center justify-center gap-2 rounded-lg font-medium select-none " +
  // neutral focus ring (no blue)
  "transition-all focus-visible:outline-none focus-visible:ring-2 " +
  "focus-visible:ring-gray-900 focus-visible:ring-offset-2 focus-visible:ring-offset-white " +
  "dark:focus-visible:ring-gray-200 dark:focus-visible:ring-offset-gray-900 " +
  "disabled:opacity-50 disabled:pointer-events-none active:translate-y-[0.5px]";

const sizes: Record<ButtonSize, string> = {
  xs: "h-8 px-2.5 text-xs",
  sm: "h-9 px-3 text-sm",
  md: "h-10 px-4 text-sm",
  lg: "h-11 px-5 text-base",
};

// Single neutral palette + a minimal danger
const tone = {
  neutral: {
    solid:
      "bg-gray-900 text-white hover:bg-black shadow-sm " +
      "dark:bg-gray-100 dark:text-gray-900 dark:hover:bg-[var(--sc-surface)]",
    outline:
      "border border-gray-300 text-gray-900 hover:bg-gray-50 " +
      "dark:border-gray-700 dark:text-gray-100 dark:hover:bg-gray-800/60",
    ghost:
      "bg-transparent text-gray-900 hover:bg-gray-100 " +
      "dark:text-gray-100 dark:hover:bg-gray-800/60",
    link: "p-0 h-auto text-gray-900 hover:underline dark:text-gray-100",
  },
  // alias 'primary' to the same neutral styles (kept for API compatibility)
  primary: {} as any,
  danger: {
    // red text only, transparent background
    solid:
      "bg-transparent text-red-600 hover:bg-red-50 " +
      "dark:text-red-400 dark:hover:bg-red-900/20",
    outline:
      "bg-transparent text-red-600 hover:bg-red-50 " +
      "dark:text-red-400 dark:hover:bg-red-900/20",
    ghost:
      "bg-transparent text-red-600 hover:bg-red-50 " +
      "dark:text-red-400 dark:hover:bg-red-900/20",
    link: "p-0 h-auto text-red-600 hover:underline dark:text-red-400",
  },
} as const;

// copy neutral styles into primary (so tone:'primary' behaves like neutral)
(tone.primary as any) = tone.neutral;

export function buttonClasses({
  variant = "solid",
  tone: t = "neutral",
  size = "md",
  block = false,
  iconOnly = false,
  className,
  loading = false,
}: {
  variant?: ButtonVariant;
  tone?: ButtonTone;
  size?: ButtonSize;
  block?: boolean;
  iconOnly?: boolean;
  className?: string;
  loading?: boolean;
}) {
  const sizeCls = iconOnly
    ? { xs: "h-8 w-8", sm: "h-9 w-9", md: "h-10 w-10", lg: "h-11 w-11" }[size]
    : sizes[size];

  return cx(
    baseBtn,
    sizeCls,
    tone[t][variant],
    block && "w-full",
    loading && "cursor-wait",
    className
  );
}

/* Drop-in shortcuts (compatible names) */
export const btn = {
  // Amber CTA — matches Button variant="default"
  primary:
    "inline-flex items-center justify-center gap-2 rounded-lg font-medium select-none transition-all " +
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--sc-amber)] focus-visible:ring-offset-2 " +
    "disabled:opacity-50 disabled:pointer-events-none " +
    "h-10 px-4 text-sm bg-[var(--sc-amber)] text-[var(--sc-text)] hover:bg-[#F0A020] shadow-sm",
  ghost: buttonClasses({ variant: "ghost", tone: "neutral", size: "md" }),
  outline: buttonClasses({ variant: "outline", tone: "neutral", size: "md" }),
  danger:
    "inline-flex items-center justify-center gap-2 rounded-lg font-medium select-none transition-all " +
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--sc-red)] focus-visible:ring-offset-2 " +
    "disabled:opacity-50 disabled:pointer-events-none " +
    "h-10 px-4 text-sm text-[var(--sc-red)] hover:bg-[var(--sc-red-bg)] border border-[#F0D0D0]",
  icon: buttonClasses({ variant: "ghost", tone: "neutral", size: "md", iconOnly: true }),
  sm: "h-9 px-3 text-[13px]",
};


export const Spinner = ({ className = "h-4 w-4" }: { className?: string }) =>
  React.createElement(
    "svg",
    {
      className: cx("animate-spin", className),
      viewBox: "0 0 24 24",
      // TS is fine with ARIA data attributes as string keys:
      ["aria-hidden"]: true,
      role: "img",
    } as React.SVGProps<SVGSVGElement>,
    React.createElement("circle", {
      className: "opacity-25",
      cx: 12,
      cy: 12,
      r: 10,
      stroke: "currentColor",
      strokeWidth: 4,
      fill: "none",
    }),
    React.createElement("path", {
      className: "opacity-75",
      fill: "currentColor",
      d: "M4 12a8 8 0 0 1 8-8v4a4 4 0 0 0-4 4H4z",
    })
  );

